'use client'

import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react'
import { sdk } from '@farcaster/miniapp-sdk'

interface SafeAreaInsets {
  top: number
  bottom: number
  left: number
  right: number
}

interface MiniAppClient {
  platformType?: 'web' | 'mobile'
  clientFid?: number
  added?: boolean
  safeAreaInsets?: SafeAreaInsets
  notificationDetails?: {
    url: string
    token: string
  }
}

interface MiniAppContext {
  user?: {
    fid: number
    username?: string
    displayName?: string
    pfpUrl?: string
  }
  location?: Record<string, unknown>
  client?: MiniAppClient
  features?: Record<string, unknown>
}

type FrameContextValue = {
  context: MiniAppContext | Record<string, unknown> | null
  isInMiniApp: boolean
  isFrameReady: boolean
}

const FrameContext = createContext<FrameContextValue>({
  context: null,
  isInMiniApp: false,
  isFrameReady: false,
})

interface EthereumWindow extends Window {
  ethereum?: {
    request: (args: { method: string; params?: unknown[] }) => Promise<unknown>
    __agcEipPatched?: boolean
  }
}

/**
 * AGC: Access the Farcaster Mini App context (user/client/frame readiness flags).
 */
export const useFrameContext = () => useContext(FrameContext)

const shouldPatchEthereum = () => {
  if (typeof window === 'undefined') {
    return false
  }
  const appContext = import.meta.env.VITE_APP_CONTEXT
  return appContext === 'baseapp' || appContext === 'farcaster'
}

/**
 * AGC: Frame Provider – loads Farcaster Mini App SDK context and signals readiness.
 */
export default function FrameProvider({ children }: { children: ReactNode }) {
  const [context, setContext] = useState<MiniAppContext | Record<string, unknown> | null>(null)
  const [isInMiniApp, setIsInMiniApp] = useState(false)
  const [isFrameReady, setFrameReady] = useState(false)

  useEffect(() => {
    let mounted = true

    const initializeContext = async () => {
      try {
        const frameContext = await sdk.context
        if (!mounted) {
          return
        }

        setContext(frameContext)
        await sdk.actions.ready()
        setFrameReady(true)

        setTimeout(async () => {
          if (!mounted) {
            return
          }
          try {
            const miniAppResult = await sdk.isInMiniApp()
            setIsInMiniApp(Boolean(miniAppResult))
          } catch (error) {
            console.warn('AGC: Failed to determine mini-app context.', error)
          }
        }, 100)
      } catch (error) {
        console.warn('AGC: Failed to initialize Farcaster Mini App context.', error)
        if (mounted) {
          setContext({ error: 'Failed to initialize Farcaster Mini App context.' })
          setIsInMiniApp(false)
        }
      }
    }

    initializeContext()

    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    if (!shouldPatchEthereum() || typeof window === 'undefined') {
      return
    }

    const ethWindow = window as EthereumWindow
    const ethereum = ethWindow.ethereum

    if (!ethereum || typeof ethereum.request !== 'function' || ethereum.__agcEipPatched) {
      return
    }

    const originalRequest = ethereum.request.bind(ethereum)

    ethereum.request = async (args) => {
      if (args && typeof args === 'object' && 'method' in args && args.method === 'eip6963RequestProvider') {
        console.warn('⚠️ AGC: Ignoring eip6963RequestProvider inside Base Mini App context.')
        return []
      }
      return originalRequest(args as { method: string; params?: unknown[] })
    }

    ethereum.__agcEipPatched = true

    return () => {
      ethereum.request = originalRequest
      delete ethereum.__agcEipPatched
    }
  }, [])

  const value = useMemo(
    () => ({
      context,
      isInMiniApp,
      isFrameReady,
    }),
    [context, isInMiniApp, isFrameReady],
  )

  return <FrameContext.Provider value={value}>{children}</FrameContext.Provider>
}

